import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldQuestion, Copy, Check, Loader2, Lock, Send, KeyRound, Eye,
  ShieldCheck, ArrowDown, FilePlus, User, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, apiError } from "../lib/api";
import { createSecureRequestKeys } from "../lib/crypto";
import { generateRecoveryToken } from "../lib/anonymous";
import { expirationOptions } from "../lib/expirations";
import { copyToClipboard } from "../lib/clipboard";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const StepCard = ({ icon: Icon, n, title, children, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-slate-50 border border-slate-200 px-5 py-4">
    <div className="flex items-center gap-2 mb-1">
      <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-emerald-600" />
      </div>
      <div className="text-sm font-semibold text-slate-900">{n}. {title}</div>
    </div>
    <div className="text-xs text-slate-500 leading-relaxed space-y-1.5 pl-9">{children}</div>
  </div>
);

const SchemaRow = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-xs text-slate-600">
    <div className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0">
      <Icon className="w-3 h-3 text-emerald-600" />
    </div>
    {label}
  </div>
);

const L = (t, k) => t(k, { returnObjects: true });

const WorkflowExplainer = ({ user, hasResult }) => {
  const { t } = useTranslation("secureRequest");
  const SCHEMA_ICONS = [User, Send, User, Lock, ShieldQuestion, KeyRound, Check];
  return (
  <motion.aside
    data-testid="sr-workflow"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: 0.08 }}
    className="lg:col-span-5 lg:sticky lg:top-32 space-y-4"
  >
    {hasResult && (
      <div data-testid="sr-next-steps" className="glass rounded-2xl p-6 border border-emerald-200">
        <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
          <Send className="w-4 h-4 text-emerald-600" /> {t("create.nextTitle")}
        </h3>
        <ol className="text-sm text-slate-600 leading-relaxed space-y-2 list-decimal pl-5">
          {L(t, "create.next").map((x) => <li key={x}>{x}</li>)}
        </ol>
        {!user && <p className="mt-3 text-sm font-semibold text-amber-700">{t("create.dontLose")}</p>}
      </div>
    )}

    <div className="glass rounded-2xl p-6">
      <h3 className="font-heading text-lg font-semibold mb-1">{t("create.howTitle")}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{t("create.howText")}</p>
    </div>

    <StepCard icon={FilePlus} n="1" title={t("create.s1Title")} testid="sr-workflow-step-1">
      <p>{t("create.s1Intro")}</p>
      <ul className="list-disc pl-4 space-y-0.5">{L(t, "create.s1Items").map((x) => <li key={x}>{x}</li>)}</ul>
      <p>{t("create.s1Then")}</p>
      <p className="text-[11px] text-amber-600">{t("create.s1Warn")}</p>
    </StepCard>

    <StepCard icon={Send} n="2" title={t("create.s2Title")} testid="sr-workflow-step-2">
      {L(t, "create.s2").map((x) => <p key={x}>{x}</p>)}
    </StepCard>

    <StepCard icon={Lock} n="3" title={t("create.s3Title")} testid="sr-workflow-step-3">
      {L(t, "create.s3").map((x) => <p key={x}>{x}</p>)}
      <Link to="/securite" className="inline-block text-emerald-600 hover:text-emerald-500 underline">{t("create.s3Link")}</Link>
    </StepCard>

    <StepCard icon={KeyRound} n="4" title={t("create.s4Title")} testid="sr-workflow-step-4">
      {L(t, "create.s4").map((x) => <p key={x}>{x}</p>)}
      {!user && <p className="text-amber-600 font-medium">{t("create.s4Anon")}</p>}
    </StepCard>

    <StepCard icon={Eye} n="5" title={t("create.s5Title")} testid="sr-workflow-step-5">
      {L(t, "create.s5").map((x) => <p key={x}>{x}</p>)}
    </StepCard>

    <div data-testid="sr-workflow-trust" className="glass rounded-2xl p-6 border border-emerald-200/70">
      <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
      </div>
      <h3 className="font-heading font-semibold text-slate-900 mb-2">{t("create.trustTitle")}</h3>
      <p className="text-sm text-slate-500 leading-relaxed mb-2">{t("create.trustIntro")}</p>
      <ul className="text-sm text-slate-500 leading-relaxed list-disc pl-5 space-y-1">
        {L(t, "create.trust").map((x) => <li key={x}>{x}</li>)}
      </ul>
      <Link to="/securite" className="inline-block mt-3 text-sm text-emerald-600 hover:text-emerald-500 underline">{t("create.trustLink")}</Link>
    </div>

    <div data-testid="sr-workflow-schema" className="rounded-xl bg-slate-50 border border-slate-200 px-5 py-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">{t("create.schemaTitle")}</div>
      <div className="space-y-1.5">
        {L(t, "create.schema").map((label, i, arr) => (
          <div key={label}>
            <SchemaRow icon={SCHEMA_ICONS[i]} label={label} />
            {i < arr.length - 1 && <ArrowDown className="w-3 h-3 text-slate-300 ml-2.5 my-0.5" />}
          </div>
        ))}
      </div>
    </div>

    {!user ? (
      <div data-testid="sr-workflow-anon" className="rounded-xl border border-amber-200 bg-amber-50/70 px-5 py-4">
        <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1"><Clock className="w-4 h-4" /> {t("create.anonTitle")}</div>
        <ul className="text-xs text-amber-700 leading-relaxed list-disc pl-5 space-y-1">
          {L(t, "create.anon").map((x) => <li key={x}>{x}</li>)}
        </ul>
      </div>
    ) : (
      <div data-testid="sr-workflow-connected" className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-5 py-4">
        <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-1"><User className="w-4 h-4" /> {t("create.connectedTitle")}</div>
        <p className="text-xs text-emerald-700 leading-relaxed">{t("create.connected")}</p>
      </div>
    )}
  </motion.aside>
  );
};

export const SecureRequestCreate = () => {
  const { user } = useAuth();
  const { t } = useTranslation("secureRequest");
  const isPaid = !!user && ["pro", "team", "business", "enterprise"].includes(user.plan);
  const [maxExp, setMaxExp] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [expiration, setExpiration] = useState(user ? "24h" : "1h");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({ pub: false, priv: false });

  // Durées réellement autorisées par les entitlements du workspace (jamais d'option refusée par le backend).
  useEffect(() => {
    if (!isPaid) return;
    let active = true;
    api.get("/account/entitlements").then(({ data }) => {
      if (active) setMaxExp(data?.max_secret_expiration || null);
    }).catch(() => {});
    return () => { active = false; };
  }, [isPaid]);

  const EXPIRATIONS = expirationOptions({ user, isPaid, maxSecretExpiration: maxExp });

  // Garde la durée sélectionnée dans les options autorisées.
  useEffect(() => {
    if (!EXPIRATIONS.some((e) => e.key === expiration)) {
      setExpiration(EXPIRATIONS.some((e) => e.key === "1h") ? "1h" : EXPIRATIONS[EXPIRATIONS.length - 1]?.key || "1h");
    }
  }, [EXPIRATIONS, expiration]);

  const submit = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const keys = await createSecureRequestKeys();
      // Utilisateur anonyme : preuve de possession (recovery token) — seul son hash est envoyé.
      let recovery = null;
      const payload = {
        title: title.trim(),
        message: message.trim() || null,
        expiration,
        crypto_version: keys.crypto_version,
        public_key_jwk: keys.public_key_jwk,
        encrypted_private_key: keys.encrypted_private_key,
        private_key_iv: keys.private_key_iv,
      };
      if (!user) {
        recovery = await generateRecoveryToken();
        payload.recovery_token_hash = recovery.tokenHash;
      }
      const { data } = await api.post("/secure-requests", payload);
      // La clé de wrap (et le recovery token anonyme) ne partent JAMAIS au backend :
      // ils vivent uniquement dans le fragment du lien privé.
      const recoveryLink = user
        ? `${data.recovery_link}#${keys.wrap_key}`
        : `${data.recovery_link}#k=${encodeURIComponent(keys.wrap_key)}&r=${encodeURIComponent(recovery.token)}`;
      setResult({ publicLink: data.public_link, recoveryLink });
      toast.success(t("create.created"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const doCopy = async (which, value) => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied((c) => ({ ...c, [which]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [which]: false })), 2000);
      toast.success(t("create.linkCopied"));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
      <div className="lg:col-span-7">
        {!result ? (
          <motion.div data-testid="sr-create-form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 sm:p-8">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
              <ShieldQuestion className="w-5 h-5 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">{t("create.title")}</h1>
            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              {t("create.intro")}
            </p>

            <label className="text-sm font-medium text-slate-700">{t("create.titleLabel")}</label>
            <Input data-testid="sr-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} placeholder={t("create.titlePh")} className="mt-1 mb-4 bg-white border-slate-200" />

            <label className="text-sm font-medium text-slate-700">{t("create.messageLabel")} <span className="text-slate-400 font-normal">{t("create.optional")}</span></label>
            <Textarea data-testid="sr-message-input" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} placeholder={t("create.messagePh")} className="mt-1 bg-white border-slate-200" rows={3} />
            <p className="text-[11px] text-amber-600 mb-4 mt-1">{t("create.messageWarn")}</p>

            <label className="text-sm font-medium text-slate-700">{t("create.expiration")}</label>
            <Select value={expiration} onValueChange={setExpiration}>
              <SelectTrigger data-testid="sr-expiration-select" className="mt-1 mb-6 bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPIRATIONS.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>

            {!user && (
              <p data-testid="sr-anon-notice" className="text-[12px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4 -mt-2">
                {t("create.anonNotice")}
              </p>
            )}

            <Button data-testid="sr-create-btn" onClick={submit} disabled={!title.trim() || creating} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              {t("create.submit")}
            </Button>
          </motion.div>
        ) : (
          <motion.div data-testid="sr-create-result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <div className="flex items-center gap-2 text-emerald-600 font-semibold mb-2"><Check className="w-5 h-5" /> {t("create.doneTitle")}</div>
              <label className="text-sm font-medium text-slate-700">{t("create.publicLabel")}</label>
              <p className="text-[11px] text-slate-500 mb-1">{t("create.publicHelp")}</p>
              <div data-testid="sr-public-link" className="ph-no-capture font-mono-token text-xs break-all bg-white rounded-lg p-3 border border-slate-200 my-2">{result.publicLink}</div>
              <Button data-testid="sr-copy-public" onClick={() => doCopy("pub", result.publicLink)} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                {copied.pub ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} {t("create.copyLink")}
              </Button>
            </div>

            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-700 font-semibold mb-1"><Lock className="w-4 h-4" /> {t("create.privateTitle")}</div>
              <p className="text-[11px] text-amber-700 mb-2">
                {t("create.privateHelp")}{" "}
                {user
                  ? t("create.privateUser")
                  : t("create.privateAnon")}
              </p>
              <div data-testid="sr-recovery-link" className="ph-no-capture font-mono-token text-xs break-all bg-white rounded-lg p-3 border border-amber-200 mb-2">{result.recoveryLink}</div>
              <Button data-testid="sr-copy-recovery" onClick={() => doCopy("priv", result.recoveryLink)} variant="outline" className="w-full border-amber-300 bg-white hover:bg-amber-100 text-amber-800 font-semibold">
                {copied.priv ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} {t("create.copyPrivate")}
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      <WorkflowExplainer user={user} hasResult={!!result} />
    </div>
  );
};
